import { DatePicker, Form, FormInstance, Select, Tooltip } from 'antd';
import workspaceService from 'services/jobService';
import RevxFlexLayout from '../RevxFlexLayout';
import { useDebounceFn } from 'ahooks';
import {
  JobStatusFilterSeparateTabEnum,
  JobUIStatusEnum,
  JobWorkflowStatusEnum,
  JobWorkspaceFilterTypeEnum,
} from 'domains/workspaceDomain';
import { DEBOUNCE_TIME } from 'utils/constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OptionData } from 'rc-select/lib/interface';
import { clone } from 'lodash-es';
import { Moment } from 'moment';
import classNames from 'classnames';
import { FilterTipsIcon } from 'components/Icons';
import { formatLocalDate } from 'utils/date';
import { OrderHistorySearchGroupEnum } from 'domains/orderDomain';
import { DeleteIcon } from 'components/Icons';
import { convertToTitleCase } from 'utils/misc';
import { useRole } from 'hooks';
import { useOrg } from 'hooks';
import { useAppSelector } from 'app/hooks';
import { OrgTypeEnum } from 'features/Platform/components/OrgAdmin/OrgTypeSelectModal';
import {
  INDUSTRY_AVG_ID,
  PLATFORM_ORG_ID,
  REVSHOP_STORE_ID,
} from 'domains/orgDomain';
import './index.less';

type Props = {
  filterType: JobWorkspaceFilterTypeEnum;
  callback: (filter: FilterOptionsParams) => void;
  displayTotal?: number;
  total?: number;
  isOrderList?: boolean;
  statusFilter?: JobUIStatusEnum | JobWorkflowStatusEnum;
  defaultDate?: [Moment, Moment];
  searchGroup?: OrderHistorySearchGroupEnum;
  isProjectList?: boolean;
  statusFilterSeparateTab?: JobStatusFilterSeparateTabEnum;
  showTotal?: boolean;
  excludeProjectId?: number;
  showSupplierFilter?: boolean;
  /** pre-fills the job or order number search and runs it, for deep links into a single job */
  initialJobOrOrderNumber?: string;
  onClose?: () => void;
};

const { RangePicker } = DatePicker;

export enum FilterLayoutTypeEnum {}

export enum FilterOptionsEnum {
  jobOrOrderNumber = 'jobOrOrderNumber',
  jobName = 'jobName',
  supplierName = 'supplierName',
  requesterName = 'requesterName',
  orderDate = 'orderDate',
  deliveryDate = 'deliveryDate',
  workflowStatus = 'workflowStatus',
  customerOrgId = 'customerOrgId',
  salesRep = 'salesRep',
}

export type FilterOptionsParams = {
  jobOrOrderNumber?: string;
  jobName?: string;
  supplierName?: string;
  requesterName?: string;
  startOrderDate?: string;
  endOrderDate?: string;
  startDeliveryDate?: string;
  endDeliveryDate?: string;
  workflowStatus?: string;
  customerOrgId?: number;
  jobId?: string;
  /* project search */
  jobDoc?: string;
  salesRep?: string;
  rfqQueryType?: string;
};

const {
  jobName,
  jobOrOrderNumber,
  supplierName,
  requesterName,
  orderDate,
  deliveryDate,
  workflowStatus,
  customerOrgId,
  salesRep,
} = FilterOptionsEnum;

const {
  getJobListFilterJobNumber,
  getJobListFilterJobName,
  getJobListFilterSupplierName,
  getJobListFilterRequesterName,
  getJobListFilterStatus,
} = workspaceService;
const LIMIT_LEN = 5;

const RevxJobListFilter = ({
  displayTotal = 0,
  total = 0,
  isOrderList,
  statusFilter,
  defaultDate,
  searchGroup,
  statusFilterSeparateTab,
  filterType,
  isProjectList = false,
  showTotal = true,
  excludeProjectId,
  showSupplierFilter,
  initialJobOrOrderNumber,
  callback,
  onClose,
}: Props) => {
  if (isOrderList) {
    statusFilter = undefined;
  }
  const [optionList, setOptionList] = useState<
    Record<FilterOptionsEnum, OptionData[]>
  >({
    [jobName]: [],
    [jobOrOrderNumber]: [],
    [supplierName]: [],
    [requesterName]: [],
    [orderDate]: [],
    [deliveryDate]: [],
    [workflowStatus]: [],
    [customerOrgId]: [],
    [salesRep]: [],
  });
  const formRef = useRef<FormInstance>(null);
  const { sourcerRole } = useRole();
  const [filterForm] = Form.useForm();

  useEffect(() => {
    if (!initialJobOrOrderNumber) {
      return;
    }
    filterForm.setFieldsValue({
      [jobOrOrderNumber]: initialJobOrOrderNumber,
    });
    // setFieldsValue does not fire onValuesChange, so the search has to be run here
    callback({ jobOrOrderNumber: initialJobOrOrderNumber });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobOrOrderNumber]);
  const [dateActive, setDateActive] = useState(false);
  const org = useOrg();
  const orgList = useAppSelector((state) => state.org.list);

  // 判断是否显示组织过滤选项（只在 SAAS 组织中显示）
  const showOrgFilter = org?.orgType === OrgTypeEnum.SAAS;

  // 获取可用的组织选项（排除平台组织等）
  const orgOptions = useMemo(() => {
    return orgList
      .filter(
        (item) =>
          item.id !== PLATFORM_ORG_ID &&
          item.id !== INDUSTRY_AVG_ID &&
          item.id !== REVSHOP_STORE_ID
      )
      .map((item) => ({
        label: item.name,
        value: item.id,
      }));
  }, [orgList]);

  const salesRepOptions = useMemo(() => {
    return orgList
      .filter(
        (item) =>
          item.id !== PLATFORM_ORG_ID &&
          item.id !== INDUSTRY_AVG_ID &&
          item.id !== REVSHOP_STORE_ID
      )
      .filter((item) => item.salesRep)
      .map((item) => ({
        label: item.salesRep as string,
        value: item.salesRep as string,
      }));
  }, [orgList]);

  const filterLabelMap = useMemo(() => {
    if (isProjectList) {
      return {
        [jobOrOrderNumber]: 'Job or order number:',
        [jobName]: 'Job type:',
        [workflowStatus]: 'Status:',
        [deliveryDate]: 'Date range for delivery date:',
        [orderDate]: 'Date range for job ordered:',
      };
    } else {
      return {};
    }
  }, [isProjectList]);

  const filterParams = (prefix: string) => ({
    limit: LIMIT_LEN,
    statusFilter,
    prefix,
    searchGroup,
    statusFilterSeparateTab,
    filterType,
    excludeProjectId,
  });
  const filterApiList = [
    {
      api: (prefix: string) => getJobListFilterJobName(filterParams(prefix)),
      type: jobName,
    },
    {
      api: (prefix: string) => getJobListFilterJobNumber(filterParams(prefix)),
      type: jobOrOrderNumber,
    },
    {
      api: (prefix: string) =>
        getJobListFilterSupplierName(filterParams(prefix)),
      type: supplierName,
    },
    {
      api: (prefix: string) =>
        getJobListFilterRequesterName(filterParams(prefix)),
      type: requesterName,
    },
    {
      api: (prefix: string) => getJobListFilterStatus(filterParams(prefix)),
      type: workflowStatus,
    },
  ];

  useEffect(() => {
    filterForm.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGroup, isOrderList, statusFilter, statusFilterSeparateTab]);

  useEffect(() => {
    setDateActive(!!defaultDate);
  }, [defaultDate]);

  useEffect(() => {
    filterApiList.forEach(async (i) => {
      optionList[i.type] = await i.api('');
      setOptionList(clone(optionList));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const runDebounceSearchHandler = async (
    prefix: string,
    type: FilterOptionsEnum
  ) => {
    cancelOnSearchHandler();
    const formV = filterForm.getFieldsValue();
    if (formV[type]) {
      const emptyObj = { [type]: undefined };
      filterForm.setFieldsValue(emptyObj);
      callback({ ...formV, ...emptyObj });
    }
    await runOnSearchHandler(prefix, type);
  };

  const { run: runOnSearchHandler, cancel: cancelOnSearchHandler } =
    useDebounceFn(
      async (prefix: string, type: FilterOptionsEnum) => {
        await onSearchHandler(prefix, type);
      },
      { wait: DEBOUNCE_TIME }
    );
  const onSearchHandler = async (prefix: string, type: FilterOptionsEnum) => {
    const api = filterApiList.find((i) => i.type === type)?.api;
    let v = await (api && api(prefix));
    if (v) {
      if (type === workflowStatus) {
        v = v.map((i) => {
          return { ...i, label: convertToTitleCase([i.label as string]) };
        });
      }
      optionList[type] = v;
      setOptionList(clone(optionList));
    }
  };

  const blurHandler = () => {
    const form = formRef.current;
    if (!form) return;
    Object.keys(form.getFieldsValue()).forEach((fieldName) =>
      form.getFieldInstance(fieldName)?.blur()
    );
  };

  const showSupplierItem =
    (sourcerRole && !isProjectList) || showSupplierFilter;

  return (
    <div
      className={classNames('job-list-filter', {
        'hide-supplier': !sourcerRole && !isProjectList && !showSupplierFilter,
        'date-active': dateActive,
        'is-project-list': isProjectList,
        'show-org-filter': showOrgFilter,
      })}
    >
      <RevxFlexLayout
        className={
          showOrgFilter
            ? 'job-list-filter__selector'
            : 'job-list-filter__selector_withoutorg'
        }
        align="center"
        // justify="justify-start"
      >
        {isProjectList && <div className="mr-20">Filter by:</div>}
        <Form
          ref={formRef}
          form={filterForm}
          layout="inline"
          initialValues={{ [orderDate]: defaultDate }}
          onValuesChange={(values) => {
            const transformedValues = Object.entries(values).reduce(
              (acc, [key, value]) => {
                if (key === orderDate) {
                  const [startDate, endDate] = Array.isArray(value)
                    ? value.map(formatLocalDate)
                    : [undefined, undefined];
                  acc.startOrderDate = startDate;
                  acc.endOrderDate = endDate;
                  setDateActive(!!value);
                }
                if (key === deliveryDate) {
                  const [startDeliveryDate, endDeliveryDate] = Array.isArray(
                    value
                  )
                    ? value.map(formatLocalDate)
                    : [undefined, undefined];
                  acc.startDeliveryDate = startDeliveryDate;
                  acc.endDeliveryDate = endDeliveryDate;
                }
                if (key === customerOrgId) {
                  acc.customerOrgId = value as number;
                } else {
                  acc[key as keyof FilterOptionsParams] = value as any;
                }
                return acc;
              },
              {} as FilterOptionsParams
            );

            callback(transformedValues);
          }}
        >
          {showOrgFilter && (
            <div className="form-item-wrapper">
              <Form.Item name={customerOrgId}>
                <Select
                  showSearch={true}
                  options={orgOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder="Organization"
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}

          {showOrgFilter && (
            <div className="form-item-wrapper">
              <Form.Item name={salesRep}>
                <Select
                  showSearch={true}
                  options={salesRepOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder="Sales Rep"
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}
          <div className="form-item-wrapper">
            <div className="filter-label">
              {filterLabelMap['jobOrOrderNumber']}
            </div>
            <Form.Item name={jobOrOrderNumber}>
              <Select
                showSearch={true}
                options={optionList[jobOrOrderNumber]}
                onSearch={async (v) => {
                  await runDebounceSearchHandler(v, jobOrOrderNumber);
                }}
                onClear={async () => {
                  await runOnSearchHandler('', jobOrOrderNumber);
                }}
                onChange={() => {
                  blurHandler();
                }}
                placeholder={
                  isProjectList ? 'Enter number' : 'Job ID or Order ID'
                }
                allowClear={true}
              />
            </Form.Item>
          </div>
          {!isProjectList && (
            <div className="form-item-wrapper">
              <div className="filter-label">{filterLabelMap['jobName']}</div>
              <Form.Item name={jobName}>
                <Select
                  showSearch={true}
                  options={optionList[jobName]}
                  onSearch={async (v) => {
                    await runDebounceSearchHandler(v, jobName);
                  }}
                  onClear={async () => {
                    await runOnSearchHandler('', jobName);
                  }}
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder={isProjectList ? 'Enter name' : 'Job name'}
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}
          {isProjectList && (
            <div className="form-item-wrapper">
              <div className="filter-label">
                {filterLabelMap['workflowStatus']}
              </div>
              <Form.Item name={workflowStatus}>
                <Select
                  showSearch={true}
                  options={optionList[workflowStatus]}
                  onSearch={async (v) => {
                    await runDebounceSearchHandler(v, workflowStatus);
                  }}
                  onClear={async () => {
                    await runOnSearchHandler('', workflowStatus);
                  }}
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder="Enter status"
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}
          {showSupplierItem && (
            <div className="form-item-wrapper">
              <Form.Item name={supplierName}>
                <Select
                  showSearch={true}
                  options={optionList[supplierName]}
                  onSearch={async (v) => {
                    await runDebounceSearchHandler(v, supplierName);
                  }}
                  onClear={async () => {
                    await runOnSearchHandler('', supplierName);
                  }}
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder="Supplier"
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}

          {!isProjectList && (
            <div className="form-item-wrapper">
              <Form.Item name={requesterName}>
                <Select
                  showSearch={true}
                  options={optionList[requesterName]}
                  onSearch={async (v) => {
                    await runDebounceSearchHandler(v, requesterName);
                  }}
                  onClear={async () => {
                    await runOnSearchHandler('', requesterName);
                  }}
                  onChange={() => {
                    blurHandler();
                  }}
                  placeholder="Requestor"
                  allowClear={true}
                />
              </Form.Item>
            </div>
          )}
          <div className="form-item-wrapper">
            <div className="filter-label">{filterLabelMap['orderDate']}</div>
            <Form.Item name={orderDate}>
              <RangePicker defaultValue={defaultDate} />
            </Form.Item>
          </div>
          {isProjectList && (
            <div className="form-item-wrapper">
              <div className="filter-label">
                {filterLabelMap['deliveryDate']}
              </div>
              <Form.Item name={deliveryDate}>
                <RangePicker />
              </Form.Item>
            </div>
          )}
        </Form>
        {defaultDate && (
          <Tooltip
            title={`Update the range to view more ${
              isOrderList ? 'orders' : 'jobs'
            }.`}
          >
            <FilterTipsIcon className="tips click" />
          </Tooltip>
        )}
        {isProjectList && (
          <RevxFlexLayout className="del-icon ml-10 click" onClick={onClose}>
            <DeleteIcon />
          </RevxFlexLayout>
        )}
      </RevxFlexLayout>
      {showTotal && (
        <div className="job-list-filter__result">
          <p>
            <span>Showing </span>
            {displayTotal}
            <span> of </span>
            {total}
            <span> Total {isOrderList ? 'Orders' : 'Jobs'}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default RevxJobListFilter;
